import { CONNECTED_BACKGROUND, removeConnectedPixels } from './connected-background.js';

const MAX_INPUT_BYTES = 10 * 1024 * 1024;
const MAX_ASSET_BYTES = 700 * 1024;
const MAX_ASSET_DATA_URL_LENGTH = 700 * 1024;

export function makeCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

export function copyCanvas(source) {
  const canvas = makeCanvas(source.width, source.height);
  canvas.getContext('2d').drawImage(source, 0, 0);
  return canvas;
}

export async function loadLocalImage(file, { originalResolution = false } = {}) {
  if (!file || file.size > MAX_INPUT_BYTES || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('请选择 10 MB 以内的 JPG、PNG 或 WebP 图片。');
  }
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
  const png = bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71;
  const webp = String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
  if (!jpeg && !png && !webp) throw new Error('图片格式无法识别，请重新导出为 JPG、PNG 或 WebP。');
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = () => reject(new Error('图片无法打开，请换一张图片。')); img.src = url; });
    if (!img.naturalWidth || !img.naturalHeight) throw new Error('图片尺寸无效。');
    if (originalResolution && (img.naturalWidth * img.naturalHeight > 32000000 || Math.max(img.naturalWidth, img.naturalHeight) > 16384)) throw new Error('照片尺寸过大，请使用 3200 万像素以内、最长边不超过 16384 像素的图片。');
    const ratio = originalResolution ? 1 : Math.min(1, 1400 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = makeCanvas(img.naturalWidth * ratio, img.naturalHeight * ratio);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally { URL.revokeObjectURL(url); }
}

export function tracePolygon(ctx, points) {
  ctx.beginPath();
  points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
  ctx.closePath();
}

export function cropToPolygon(source, points) {
  if (points.length < 3) throw new Error('请至少点击三个点，再完成描边。');
  let area = 0;
  points.forEach((p, index) => { const q = points[(index + 1) % points.length]; area += p.x * q.y - p.y * q.x; });
  if (Math.abs(area) < 30) throw new Error('描边区域太小，请圈选完整图案。');
  const result = makeCanvas(source.width, source.height);
  const ctx = result.getContext('2d');
  tracePolygon(ctx, points); ctx.clip(); ctx.drawImage(source, 0, 0);
  return result;
}

export function removeMatchingColor(source, color, tolerance) {
  const result = copyCanvas(source), ctx = result.getContext('2d');
  const pixels = ctx.getImageData(0, 0, result.width, result.height);
  const limit = Math.max(0, Number(tolerance)) * 4.42;
  for (let i = 0; i < pixels.data.length; i += 4) {
    const d = Math.hypot(pixels.data[i] - color[0], pixels.data[i + 1] - color[1], pixels.data[i + 2] - color[2]);
    const factor = Math.min(1, Math.max(0, (d - limit) / 14));
    pixels.data[i + 3] = Math.round(pixels.data[i + 3] * factor);
  }
  ctx.putImageData(pixels, 0, 0);
  return result;
}

export function removeConnectedBackground(source, options = {}) {
  if (!Number.isInteger(source?.width) || !Number.isInteger(source?.height) || source.width < 1 || source.height < 1 || typeof source.getContext !== 'function') throw new Error('图片尺寸或像素数据无效。');
  if (source.width > CONNECTED_BACKGROUND.maxEdge || source.height > CONNECTED_BACKGROUND.maxEdge) throw new Error('图片尺寸过大，请使用最长边不超过 1400 像素的工作图片。');
  const context = source.getContext('2d');
  if (!context) throw new Error('图片无法打开，请换一张图片。');
  const pixels = context.getImageData(0, 0, source.width, source.height);
  const { data, removedPixels } = removeConnectedPixels(pixels, options);
  const canvas = makeCanvas(source.width, source.height), output = canvas.getContext('2d');
  const result = output.createImageData(canvas.width, canvas.height);
  result.data.set(data);
  output.putImageData(result, 0, 0);
  return { canvas, removedPixels };
}

const hexOf = rgb => '#' + rgb.map(value => Math.round(value).toString(16).padStart(2, '0')).join('');

export function sampleColorAt(source, point, radius = 1) {
  if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) throw new Error('请点选照片中的颜色。');
  const x = Math.max(0, Math.min(source.width - 1, Math.floor(point.x))), y = Math.max(0, Math.min(source.height - 1, Math.floor(point.y)));
  const ctx = source.getContext('2d');
  if (ctx.getImageData(x, y, 1, 1).data[3] < 16) throw new Error('这里是透明区域，请点选有颜色的位置。');
  const reach = Math.max(0, Math.min(2, Math.floor(radius))), left = Math.max(0, x - reach), top = Math.max(0, y - reach);
  const width = Math.min(source.width, x + reach + 1) - left, height = Math.min(source.height, y + reach + 1) - top;
  const pixels = ctx.getImageData(left, top, width, height).data, samples = [];
  for (let py = 0; py < height; py++) for (let px = 0; px < width; px++) {
    const offset = (py * width + px) * 4;
    if (pixels[offset + 3] >= 16) samples.push({ rgb: [...pixels.slice(offset, offset + 3)], distance: (left + px - x) ** 2 + (top + py - y) ** 2 });
  }
  // Return an actual nearby pixel, robust to one-pixel sensor/compression noise.
  // No lightness filtering or palette quantization: black, white and gray remain valid.
  const median = [0, 1, 2].map(channel => samples.map(sample => sample.rgb[channel]).sort((a, b) => a - b)[Math.floor(samples.length / 2)]);
  samples.sort((a, b) => a.rgb.reduce((sum, value, channel) => sum + (value - median[channel]) ** 2, 0) - b.rgb.reduce((sum, value, channel) => sum + (value - median[channel]) ** 2, 0) || a.distance - b.distance);
  return hexOf(samples[0].rgb);
}

export function dominantColor(source, region) {
  if (![region?.x, region?.y, region?.rx, region?.ry].every(Number.isFinite) || region.rx <= 0 || region.ry <= 0) throw new Error('请拖选照片中的颜色区域。');
  const left = Math.max(0, Math.floor(region.x - region.rx)), top = Math.max(0, Math.floor(region.y - region.ry));
  const right = Math.min(source.width, Math.ceil(region.x + region.rx)), bottom = Math.min(source.height, Math.ceil(region.y + region.ry));
  const width = right - left, height = bottom - top;
  if (width < 1 || height < 1) throw new Error('圈选区域太小，请重新圈选。');
  const pixels = source.getContext('2d').getImageData(left, top, width, height).data;
  const bins = new Map(), step = Math.max(1, Math.floor(Math.sqrt(width * height / 40000)));
  for (let y = 0; y < height; y += step) for (let x = 0; x < width; x += step) {
    if (((x + left - region.x) / region.rx) ** 2 + ((y + top - region.y) / region.ry) ** 2 > 1) continue;
    const i = (y * width + x) * 4;
    if (pixels[i + 3] < 16) continue;
    const rgb = [pixels[i], pixels[i + 1], pixels[i + 2]], weight = pixels[i + 3] / 255;
    const key = rgb.map(v => Math.floor(v / 16)).join(',');
    const bin = bins.get(key) || { weight: 0, rgb: [0, 0, 0], samples: [] };
    bin.weight += weight; bin.rgb = bin.rgb.map((value, channel) => value + rgb[channel] * weight); bin.samples.push(rgb); bins.set(key, bin);
  }
  if (!bins.size) throw new Error('这个区域是透明的，请选择有颜色的位置。');
  const dominant = [...bins.values()].sort((a, b) => b.weight - a.weight)[0], center = dominant.rgb.map(value => value / dominant.weight);
  const distance = rgb => rgb.reduce((sum, value, channel) => sum + (value - center[channel]) ** 2, 0);
  // Group similar pixels to find the representative color, but retain the
  // original pixel value instead of rounding the output to a histogram bin.
  return hexOf(dominant.samples.reduce((best, rgb) => distance(rgb) < distance(best) ? rgb : best));
}

const blobOf = (canvas, type, quality) => new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('图片导出失败，请稍后重试。')), type, quality));
const dataURLOf = blob => new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(new Error('图片读取失败。')); reader.readAsDataURL(blob); });

async function packAsset(blob, canvas) {
  if (blob.size > MAX_ASSET_BYTES) return null;
  const dataUrl = await dataURLOf(blob);
  // Match normalizeAssets: the complete data URL, including its header, must fit.
  if (dataUrl.length > MAX_ASSET_DATA_URL_LENGTH) return null;
  return { dataUrl, width: canvas.width, height: canvas.height, bytes: blob.size };
}
export async function exportTransparentAsset(source) {
  const data = source.getContext('2d').getImageData(0, 0, source.width, source.height).data;
  let left = source.width, top = source.height, right = -1, bottom = -1;
  for (let y = 0; y < source.height; y++) for (let x = 0; x < source.width; x++) if (data[(y * source.width + x) * 4 + 3] > 3) {
    left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
  }
  if (right < left) throw new Error('图案已全部擦除，请撤销或重新上传。');
  const width = right - left + 1, height = bottom - top + 1;
  for (const maxEdge of [640, 512, 384, 256]) {
    const ratio = Math.min(1, maxEdge / Math.max(width, height)), result = makeCanvas(width * ratio, height * ratio);
    result.getContext('2d').drawImage(source, left, top, width, height, 0, 0, result.width, result.height);
    const png = await blobOf(result, 'image/png');
    const pngAsset = await packAsset(png, result);
    if (pngAsset) return pngAsset;
    for (const quality of [.92, .8, .65]) {
      const webp = await blobOf(result, 'image/webp', quality);
      if (['image/webp', 'image/png'].includes(webp.type)) {
        const webpAsset = await packAsset(webp, result);
        if (webpAsset) return webpAsset;
      }
    }
  }
  throw new Error('图案细节过多，无法压缩到 700 KB，请先裁剪范围。');
}

